import { TypedMap } from 'flow/types/TypedMap';
import { CallRecord } from 'flow/types/CallRecord';
import { PageType } from 'flow/components/call-history-v2/types';
import { base64DecodeUTF8 } from 'flow/utils/transcode-utils';
import { fromJS } from 'immutable';
import {
    callRecordIsIncoming,
    callRecordIsMissed,
    getDateOfPersonalCallRecord,
    isCallRecordAMeeting,
} from 'flow/utils/call-records-utils';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { prettyFormatE164Number } from 'flow/utils/call-utils';

export const showBankidVerification = (callRecord: TypedMap<CallRecord>) => {
    if (callRecord?.getIn(['additionalAttributes', 'X-TVX-AGENT-INFO'])) {
        const decodedValue = base64DecodeUTF8(
            callRecord.getIn(['additionalAttributes', 'X-TVX-AGENT-INFO']),
        );
        return JSON.parse(decodedValue).status;
    }
    return false;
};

export type IdentityInfo = {
    orderRef: string | null;
    authenticatorType: string;
};

export const getIdentityInfo = (
    callRecord: TypedMap<CallRecord>,
): IdentityInfo | null => {
    const encoded = callRecord?.getIn([
        'additionalAttributes',
        'X-TVX-AGENT-INFO',
    ]);

    if (!encoded) {
        return null;
    }

    try {
        const decoded = JSON.parse(base64DecodeUTF8(encoded));

        return {
            orderRef: decoded?.orderRef ?? null,
            authenticatorType:
                typeof decoded?.authenticatorType === 'string'
                    ? decoded.authenticatorType
                          .toLowerCase()
                          .replace(/[_-]/g, ' ')
                    : 'bankid',
        };
    } catch {
        return null;
    }
};

export const getContactFromCallRecord = (callRecord: TypedMap<CallRecord>) => {
    if (callRecord.get('contact')) {
        return callRecord.get('contact');
    }

    const phoneNumber = parsePhoneNumberFromString(
        callRecord.getIn(['number', 'number'], ''),
    );

    return phoneNumber?.getType() === 'MOBILE'
        ? fromJS({ mobile: callRecord.get('number') })
        : fromJS({ fixed: callRecord.get('number') });
};

export const callRecordIsVideoCall = (callRecord: TypedMap<CallRecord>) =>
    callRecord?.get('DTOSubtype') === 'video-call';

export const getContactFullName = (callRecord: TypedMap<CallRecord>) =>
    [
        callRecord.getIn(['contact', 'firstName']),
        callRecord.getIn(['contact', 'lastName']),
    ]
        .join(' ')
        .trim();

/**
 * What a call-record row calls the other party: their name, or the number the
 * row shows in its place when there is no name. Mirrors CallRecordTitle, so a
 * screen reader hears what is on screen.
 *
 * Empty for a technical number, which the row renders as nothing at all.
 */
export const getCallRecordDisplayName = (
    callRecord: TypedMap<CallRecord>,
    userCountryCode?: string,
) => {
    const name = callRecord ? getContactFullName(callRecord) : '';

    if (name) {
        return name;
    }

    if (callRecord?.getIn(['number', 'type']) === 'technical_number') {
        return '';
    }

    if (callRecord?.getIn(['contact', 'type']) === 'global') {
        return callRecord.getIn(['contact', 'companyName']);
    }

    return prettyFormatE164Number(
        callRecord?.getIn(['number', 'number']),
        userCountryCode,
    );
};

/**
 * Returns the chat session id of a video conference call record, or undefined
 * for non-video calls.
 */
export const getCallRecordChatSessionId = (
    callRecord: TypedMap<CallRecord> | undefined,
) => {
    if (!callRecord || !isCallRecordAMeeting(callRecord)) return undefined;
    return callRecord.getIn(['conferenceDTO', 'chatSessionId']);
};

export const getUserFriendlyCallTypeTranslation = (
    callRecord: TypedMap<CallRecord>,
) => {
    if (isCallRecordAMeeting(callRecord)) {
        return 'Call.VideoMeeting';
    }

    if (callRecordIsIncoming(callRecord) && callRecordIsMissed(callRecord)) {
        return 'call.missed';
    }

    if (callRecordIsIncoming(callRecord)) {
        return 'call.incoming';
    }
    return 'call.outgoing';
};

// Accepts either a full TypedMap<CallRecord> or a TypedMap<SortingEntry> — both
// carry the connected/answered/disconnected/direction/ohwOrdered fields this
// function reads, so the queue/personal date-group selectors can hand it
// sortingData entries directly without a canonical join. Typed loosely because
// TypedMap is invariant in its payload, making the union of `.get` overloads
// uncallable; the Immutable runtime check is structural so this is safe.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getCallRecordDisplayTimestamp = (
    callRecord: any,
    pageType?: PageType,
) => {
    if (pageType === 'pbx' || pageType === 'tickets') {
        return (
            callRecord.get('connected') ||
            callRecord.get('answered') ||
            callRecord.get('disconnected')
        );
    }
    return getDateOfPersonalCallRecord(callRecord);
};
